import React, { useCallback, useEffect, useRef, useState } from "react"
import BasicModal from "../GlobalComponents/BasicModal"
import { IconButton } from "@mui/material"
import AddIcon from "@mui/icons-material/Add"
import { PlusButton } from "../GlobalComponents/CommonComponents"
import UserForm from "./UserRegistrationModal"
import Search from "../GlobalComponents/Search"
import { getCommonUsersData, getUsersData } from "../../services/userServices"
import Loader from "../GlobalComponents/Loader"
import UserCards from "./UserCards"
import CommonUserCards from "./CommonUserCards"
import CommonUserFormData from "./CommonUserFormData"
import { debounce } from "lodash"
import {
  PATIENTINFO,
  patientInfoSearch,
  patientTableKeys,
  tableInitialDataCount,
} from "../../utils/constants"
import { setActiveTab } from "../../redux/reducer/appReducer"
import { useDispatch } from "react-redux"
import { getPatientInvoice } from "../../services/invoiceServices"
import { getBaseStorageUrl } from "../../utils/Helpers"

function CommonUsers(props) {
  const isEditing = props.isEditing

  const formFields = {
    firstName: "",
    title: "",
    password: "",
    email: "",
    role: "",
    profileUrl: "",
    show: false,
    invoiceDetails: []
  }

  // const [formData, setFormData] = useState(formFields);
  const [usersData, setUsersData] = useState([])
  const [isLoading, setIsLoading] = useState([])
  const [loadingStatus, setLoadingStatus] = useState("hide")
  const [paginationData, setPaginationData] = useState({
    count: 0,
    endReached: false,
    searchValue: "",
  })
  // const [searchValue, setSearchValue] = useState("")
  const searchValue = paginationData?.searchValue
  const [countFetches, setCountFetches] = useState(0)
  const [endReached, setEndReached] = useState(false)
  const observerRef = useRef(null)
  const tableContainerRef = useRef(null)
  const tableLimit = tableInitialDataCount
  const dispatch = useDispatch()

  let modalFields = {
    // ...modalData,
    onClose: onCloseModal,
    onSubmit: onSubmit,
    title: "Add New User",
    show: false,
    formData: formFields,
    isEditing: false,
  }

  const [modalData, setModalData] = useState(modalFields)

  const userRef = useRef(null)

  function handleSearchChange(e) {
    setPaginationData((prev) => ({ ...prev, searchValue: e.target.value, endReached: false }))
    setEndReached(false)
  }

  const debouncedFilter = useCallback(
    debounce((searchKey) => {
      getData(countFetches, searchKey)
      setCountFetches(1)
      setEndReached(false)
    }, 300),
    []
  )

  useEffect(() => {
    if (endReached) {
      setLoadingStatus("hide")
    }
  }, [endReached])

  const callObserver = (entries) => {
    if (entries[0].isIntersecting) {
      let count = 0
      setCountFetches((prevCount) => {
        count = prevCount
        return prevCount + 1
      })
      setEndReached((prev) => {
        if (!prev && count !== 0) {
          getData(count, searchValue)
        }
        return prev
      })
    }
  }

  useEffect(() => {
    const observer = new IntersectionObserver(callObserver, { threshold: 1 })
    if (observerRef.current) {
      observer.observe(observerRef.current)
    }
    return () => {
      if (observerRef.current) {
        observer.unobserve(observerRef.current)
      }
    }
    // }, [observerRef, searchValue])
  }, [observerRef.current, paginationData?.searchValue, usersData, modalData?.show])


  useEffect(() => {
    debouncedFilter.cancel()
    debouncedFilter(searchValue)
  }, [searchValue])

  // Scroll to top when search value changes
  useEffect(() => {
    if (tableContainerRef.current) {
      // Try to find the TableContainer element within the ref
      const tableContainer = tableContainerRef.current.querySelector('.MuiTableContainer-root')
      if (tableContainer) {
        tableContainer.scrollTo({ top: 0, behavior: 'auto' })
      } else {
        // Fallback to the container div itself
        tableContainerRef.current.scrollTo({ top: 0, behavior: 'auto' })
      }
    }
  }, [searchValue])

  async function getData(offset, searchKey) {
    if (endReached) {
      return
    }

    setLoadingStatus(prev => {
      if (prev === "loaded") {
        return "loading"
      }
    })

    try {
      const data = await getCommonUsersData({
        searchKey,
        offset: offset ? offset : countFetches,
        limit: tableLimit,
      })
      const { result = [] } = data
      if (result) {
        if (result?.length < tableLimit) {
          setEndReached(true)
        }
        if (offset === 0) {
          setUsersData(result)
        } else {
          setUsersData((prev) => [...prev, ...result])
        }
      } else {
        setEndReached(true)
      }
    } catch (error) {
      console.log("getData error", error)
    }

    setIsLoading(false)
    setLoadingStatus("loaded")
  }

  async function getPatientInvoices(patientId) {
    try {
      const data = await getPatientInvoice({ patientId })

      // console.log("invoices data", { data, userRef })
      if (userRef?.current?.id === patientId) {
        let invoices = data?.result?.invoiceDetails?.length > 0 ? data.result.invoiceDetails : []
        setModalData((prev) => ({
          ...prev,
          formData: {
            ...prev.formData,
            invoiceDetails: invoices
          },
        }))

        setInvoiceData(invoices)
      }
    } catch (error) {
      console.log("getData error", error)
      setInvoiceData([])
    }

    setIsLoading(false)
  }

  useEffect(() => {
    dispatch(setActiveTab(PATIENTINFO))
  }, [])

  function onCloseModal() {
    userRef.current = null
    setInvoiceData([])
    setModalData((prev) => ({ ...prev, show: false, formData: formFields, isEditing: false }))
  }

  function resetSearch() {
    setPaginationData((prev) => ({ ...prev, searchValue: "", endReached: false }))
    setEndReached(false)
  }


  function onSubmit() {
    onCloseModal()
    getData(0, "")
    setEndReached(false)
    setIsLoading(true)
    resetSearch()
  }

  // let modalData = {};

  function handleEdit(selectedData) {
    // console.log("selectedData: 123", selectedData)
    getPatientInvoices(selectedData?._id)
    userRef.current = { id: selectedData?._id }

    setModalData((prev) => ({
      ...prev,
      show: true,
      title: "Edit User",
      formData: selectedData,
      isEditing: true,
    }))
  }

  const [invoiceData, setInvoiceData] = useState([])

  // useEffect(() => {
  //   // console.log("invoiceData", invoiceData)
  //   if (modalData?.formData?._id === userRef?.current?.id) {
  //     setModalData((prev) => ({
  //       ...prev,
  //       formData: {
  //         ...prev.formData,
  //         invoiceDetails: invoiceData,
  //       },
  //     }))
  //   }


  // }, [invoiceData, modalData?.formData?.invoiceDetails])
  // console.log("invoices data 123", { invoiceData, formData: modalData?.formData })


  console.log("usersDatausersData", usersData)
  // console.log("modalData123:", modalData?.formData?.invoiceDetails)

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        padding: "20px 40px",
        overflow: "hidden",
      }}
    >
      {/* <div>
        <h1>Common Users</h1>
        <img src="http://localhost:5008/storage/file/0ed19e0a-3e49-4fa2-8f5a-03b89d84717f_new.png" alt="test"
          style={{ width: "100px", height: "100px" }} />
        <img src="/storage/file/0ed19e0a-3e49-4fa2-8f5a-03b89d84717f_new.png" alt="test"
          style={{ width: "100px", height: "100px" }} />
        <div>
          <p>{getBaseStorageUrl()}</p>
          <img src={`${getBaseStorageUrl()}/file/0ed19e0a-3e49-4fa2-8f5a-03b89d84717f_new.png`} alt="test"
            style={{ width: "100px", height: "100px" }} />
        </div>
      </div> */}
      {!modalData.show && (
        <div
          style={{
            marginBottom: "20px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div
            className="search-container"
          >
            <Search
              addStyle={{ width: "100%" }}
              searchValue={searchValue}
              handleSearchChange={handleSearchChange}
              onClose={resetSearch}
              placeholder={patientInfoSearch}
            />
          </div>

          <div style={{ justifySelf: "flex-end", marginLeft: "auto" }}>
            <PlusButton
              onClickPlus={() =>
                setModalData((prev) => ({
                  ...prev,
                  show: true,
                  title: "Add New User",
                  isEditing: false,
                }))
              }
            />
          </div>
        </div>
      )}

      {isLoading && <Loader />}

      {modalData.show ? (
        <CommonUserFormData {...modalData} setModalData={setModalData} />
      ) : (
        <CommonUserCards
          usersData={usersData}
          handleEdit={handleEdit}
          isLoading={isLoading}
          searchValue={paginationData?.searchValue}
          headerKeys={patientTableKeys}
          observer={observerRef}
          loadingStatus={loadingStatus}
          tableContainerRef={tableContainerRef}
        />
      )}
    </div>
  )
}

export default CommonUsers
